import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { Target, Plus, Trophy, Flame, TrendingUp, Trash2, CalendarCheck, Sparkles, FileDown, Share2, Copy, Link2Off, Dumbbell, Scale, ArrowUp, ArrowDown, Minus, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import api from "@/lib/api";
import { localISODate } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { generateWeeklyPdf } from "@/lib/weeklyPdf";
import { SectionHeader, EmptyState } from "@/components/Shared";
import { Bars, LineTrend } from "@/components/Charts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress as ProgressBar } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export default function Progress() {
  const { t } = useI18n();
  return (
    <div className="space-y-10">
      <SectionHeader title={t("progress")} />
      <ProgressSummary />
      <Heatmap />
      <WeightTrend />
      <MuscleVolume />
      <ProgressiveOverload />
      <div className="grid gap-6 lg:grid-cols-2 [&>*]:min-w-0">
        <WeeklyReview />
        <BestDays />
      </div>
    </div>
  );
}

function ProgressSummary() {
  const { t, lang } = useI18n();
  const { data } = useQuery({ queryKey: ["achievements"], queryFn: async () => (await api.get("/achievements")).data });
  const tons = data?.total_volume ? (data.total_volume / 1000).toFixed(1) : "0";
  const stats = [
    { icon: Dumbbell, label: lang === "id" ? "Total Latihan" : "Total Workouts", value: data?.total_sessions ?? 0, sub: lang === "id" ? "sesi selesai" : "sessions done", accent: "bg-primary/15 text-primary" },
    { icon: Flame, label: lang === "id" ? "Streak Sekarang" : "Current Streak", value: data?.workout_streak ?? 0, sub: lang === "id" ? `terbaik: ${data?.best_streak ?? 0} hari` : `best: ${data?.best_streak ?? 0} days`, accent: "bg-orange-500/15 text-orange-500" },
    { icon: TrendingUp, label: lang === "id" ? "Total Beban" : "Total Lifted", value: `${tons}t`, sub: lang === "id" ? "ton diangkat" : "tonnes lifted", accent: "bg-sky-500/15 text-sky-500" },
    { icon: Trophy, label: lang === "id" ? "Rekor Pribadi" : "Personal Records", value: data?.total_prs ?? 0, sub: lang === "id" ? "PR tercetak" : "PRs set", accent: "bg-warning/15 text-warning" },
  ];
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="progress-summary">
      {stats.map((s, i) => (
        <div key={i} className="rounded-2xl border bg-card p-5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">{s.label}</span>
            <span className={cn("grid h-8 w-8 place-items-center rounded-lg", s.accent)}><s.icon className="h-4 w-4" /></span>
          </div>
          <div className="mt-3 font-display text-3xl font-bold tabular-nums">{s.value}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">{s.sub}</div>
        </div>
      ))}
    </div>
  );
}

function WeightTrend() {
  const { t, lang } = useI18n();
  const { data } = useQuery({ queryKey: ["body-metrics"], queryFn: async () => (await api.get("/body-metrics")).data });
  const list = [...(data || [])].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const chart = list.filter((m) => m.weight).map((m) => ({ date: (m.date || "").slice(5), weight: m.weight }));
  const first = chart[0]?.weight, last = chart[chart.length - 1]?.weight;
  const diff = first != null && last != null ? +(last - first).toFixed(1) : null;
  return (
    <div className="rounded-2xl border bg-card p-5" data-testid="weight-trend">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/15 text-primary"><Scale className="h-4.5 w-4.5" /></span>
          <h3 className="font-display text-lg font-bold tracking-tight">{lang === "id" ? "Tren Berat Badan" : "Weight Trend"}</h3>
        </div>
        {diff != null && chart.length > 1 && (
          <span className={cn("rounded-full px-3 py-1 text-sm font-semibold tabular-nums", diff <= 0 ? "bg-primary/15 text-primary" : "bg-orange-500/15 text-orange-500")}>
            {diff > 0 ? "+" : ""}{diff} kg
          </span>
        )}
      </div>
      {chart.length > 1 ? (
        <LineTrend data={chart} dataKey="weight" xKey="date" unit=" kg" height={240} />
      ) : (
        <p className="py-8 text-center text-sm text-muted-foreground">{lang === "id" ? "Catat berat badanmu di halaman Tubuh untuk melihat trennya di sini." : "Log your weight on the Body page to see the trend here."}</p>
      )}
    </div>
  );
}

const GROUP_COLORS = {
  chest: "bg-emerald-500", back: "bg-sky-500", legs: "bg-violet-500",
  shoulders: "bg-amber-500", arms: "bg-rose-500", core: "bg-cyan-500", fullbody: "bg-primary",
};

function MuscleVolume() {
  const { t } = useI18n();
  const { data } = useQuery({ queryKey: ["muscle-volume"], queryFn: async () => (await api.get("/muscle-volume")).data });
  const groups = data?.groups || [];
  const max = groups.reduce((m, g) => Math.max(m, g.volume), 0) || 1;
  return (
    <div className="rounded-2xl border bg-card p-5" data-testid="muscle-volume">
      <SectionTitle icon={Dumbbell} title={t("muscleVolumeTitle")} subtitle={t("muscleVolumeSub")}
        action={data?.total ? <span className="rounded-full bg-primary/15 px-3 py-1 text-sm font-semibold tabular-nums text-primary">{data.total.toLocaleString()} kg</span> : null} />
      {groups.length ? (
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.group} data-testid={`mv-${g.group}`}>
              <div className="mb-1 flex items-center justify-between text-sm">
                <span className="font-medium">{t(`mg_${g.group}`)}</span>
                <span className="tabular-nums text-muted-foreground">{g.volume.toLocaleString()} kg · {g.sets} {t("set").toLowerCase()}</span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-muted">
                <div className={cn("h-full rounded-full transition-all duration-700", GROUP_COLORS[g.group] || "bg-primary")}
                  style={{ width: `${Math.round((g.volume / max) * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="py-8 text-center text-sm text-muted-foreground">{t("muscleVolumeEmpty")}</p>
      )}
    </div>
  );
}

const SectionTitle = ({ icon: Icon, title, subtitle, action }) => (
  <div className="mb-4 flex items-center justify-between gap-3">
    <div className="flex items-center gap-2.5">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/15 text-primary"><Icon className="h-4.5 w-4.5" /></span>
      <div>
        <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
    {action}
  </div>
);

const OVERLOAD_ACTION = {
  increase_weight: { icon: ArrowUp, cls: "bg-emerald-500/15 text-emerald-500" },
  increase_reps: { icon: Plus, cls: "bg-sky-500/15 text-sky-500" },
  hold: { icon: Minus, cls: "bg-muted text-muted-foreground" },
  deload: { icon: ArrowDown, cls: "bg-orange-500/15 text-orange-500" },
};

function ProgressiveOverload() {
  const { t } = useI18n();
  const { data, refetch } = useQuery({ queryKey: ["overload"], queryFn: async () => (await api.get("/progressive-overload")).data });
  const [busy, setBusy] = useState(false);
  const items = data?.items;

  const generate = async () => {
    setBusy(true);
    try { await api.post("/progressive-overload/generate"); await refetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border bg-card p-5" data-testid="progressive-overload">
      <SectionTitle icon={TrendingUp} title={t("overloadTitle")} subtitle={t("overloadSub")}
        action={items && items.length ? (
          <Button size="sm" variant="outline" onClick={generate} disabled={busy} className="gap-1.5" data-testid="overload-refresh-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {t("overloadRegenerate")}
          </Button>
        ) : null} />
      {busy && !items ? (
        <div className="flex flex-col items-center gap-3 py-10 text-sm text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin text-primary" /> {t("overloadGenerating")}
        </div>
      ) : items && items.length ? (
        <div className="space-y-3">
          {items.map((it, i) => {
            const a = OVERLOAD_ACTION[it.action] || OVERLOAD_ACTION.hold;
            return (
              <div key={it.exercise_id || i} className="rounded-xl border bg-muted/30 p-4" data-testid={`overload-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display font-semibold">{it.exercise_name}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">{it.reason}</div>
                  </div>
                  <span className={cn("flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold", a.cls)}>
                    <a.icon className="h-3.5 w-3.5" /> {t(`act_${it.action}`)}
                  </span>
                </div>
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-primary/10 px-3 py-2 text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("overloadTarget")}</span>
                  <span className="font-display font-bold text-primary">{it.target}</span>
                </div>
                {it.suggestion && <p className="mt-2 text-sm text-muted-foreground">{it.suggestion}</p>}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <p className="max-w-sm text-sm text-muted-foreground">{t("overloadHint")}</p>
          <Button onClick={generate} disabled={busy} className="gap-2" data-testid="overload-generate-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {t("overloadGenerate")}
          </Button>
        </div>
      )}
    </div>
  );
}

function Heatmap() {
  const { t, lang } = useI18n();
  const { data } = useQuery({ queryKey: ["heatmap"], queryFn: async () => (await api.get("/checkins/heatmap")).data });
  const counts = data?.counts || {};
  const weeks = 26;
  const days = [];
  const today = new Date();
  const start = new Date(today); start.setDate(today.getDate() - weeks * 7 + 1);
  for (let i = 0; i < weeks * 7; i++) {
    const d = new Date(start); d.setDate(start.getDate() + i);
    const key = localISODate(d);
    days.push({ key, active: !!counts[key] });
  }
  const cols = [];
  for (let w = 0; w < weeks; w++) cols.push(days.slice(w * 7, w * 7 + 7));

  return (
    <div className="rounded-2xl border bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-display text-lg font-bold tracking-tight">{t("consistency")}</h3>
        <div className="flex items-center gap-2 rounded-full bg-primary/15 px-3 py-1 text-sm font-semibold text-primary">
          <Flame className="h-4 w-4" /> {data?.streak || 0} {t("days")}
        </div>
      </div>
      <div className="overflow-x-auto no-scrollbar">
        <div className="flex gap-1">
          {cols.map((col, ci) => (
            <div key={ci} className="flex flex-col gap-1">
              {col.map((d) => (
                <div key={d.key} title={d.key}
                  className={`h-3 w-3 rounded-sm ${d.active ? "bg-primary" : "bg-muted"}`} />
              ))}
            </div>
          ))}
        </div>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">{lang === "id" ? "Setiap kotak = satu hari. Hijau = hari kamu check-in / latihan." : "Each square = one day. Green = a day you checked in / trained."}</p>
    </div>
  );
}

function Challenges() {
  const { t } = useI18n();
  const { data } = useQuery({ queryKey: ["challenges"], queryFn: async () => (await api.get("/challenges")).data });
  const items = data || [];

  useEffect(() => {
    if (!items.length) return;
    let celebrated = [];
    try { celebrated = JSON.parse(localStorage.getItem("fp_celebrated") || "[]"); } catch { celebrated = []; }
    const newly = items.filter((c) => c.completed && !celebrated.includes(c.key));
    if (newly.length) {
      confetti({ particleCount: 70, spread: 60, origin: { y: 0.3 }, scalar: 0.9, colors: ["#10b981", "#34d399", "#a7f3d0"] });
      toast.success(`🎉 ${newly[0].title} — ${t("challengeDone")}`);
      localStorage.setItem("fp_celebrated", JSON.stringify([...celebrated, ...newly.map((c) => c.key)]));
    }
  }, [items, t]);

  return (
    <div>
      <SectionTitle icon={Sparkles} title={t("challenges")} subtitle={t("startChallengeHint")} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((c) => (
          <div key={c.key} className={cn("rounded-2xl border bg-card p-5", c.completed && "border-primary/40 bg-primary/5")} data-testid={`challenge-${c.key}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="font-medium">{c.title}</div>
              {c.completed && <span className="flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground"><Trophy className="h-3 w-3" /> {t("challengeDone")}</span>}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{c.description}</p>
            <div className="mt-4">
              <div className="mb-1.5 flex justify-between text-xs">
                <span className="text-muted-foreground">{c.percent}%</span>
                <span className="tabular-nums font-medium">{c.current} {t("of")} {c.target}</span>
              </div>
              <ProgressBar value={c.percent} className="h-2" />
              <div className="mt-1 text-[11px] text-muted-foreground">{c.days} {t("days")}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WeeklyReview() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["weekly-review"], queryFn: async () => (await api.get("/weekly-review")).data });
  const [shareUrl, setShareUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const d = data || {};

  const downloadPdf = () => {
    generateWeeklyPdf({ name: (user?.name || "").split(" ")[0], data: d, lang });
    toast.success(t("downloadPdf"));
  };

  const makeShare = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/share");
      const url = `${window.location.origin}/share/${res.token}`;
      setShareUrl(url);
      try { await navigator.clipboard.writeText(url); } catch { /* clipboard blocked */ }
      toast.success(t("shareLinkCreated"));
    } catch { toast.error("Error"); } finally { setBusy(false); }
  };

  const copyShare = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success(t("linkCopied")); } catch { /* noop */ }
  };

  const revokeShare = async () => {
    await api.delete("/share");
    setShareUrl(null);
    toast.success(t("linkRevoked"));
  };

  return (
    <div className="rounded-2xl border bg-card p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-lg font-bold tracking-tight">{t("weeklyReview")}</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={downloadPdf} className="gap-1.5" data-testid="download-pdf-btn"><FileDown className="h-4 w-4" /> PDF</Button>
          <Button size="sm" onClick={makeShare} disabled={busy} className="gap-1.5" data-testid="share-progress-btn"><Share2 className="h-4 w-4" /> {t("shareProgress")}</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Metric icon={CalendarCheck} label={t("checkIn")} value={d.checkins ?? 0} />
        <Metric icon={Flame} label={t("adherence")} value={`${d.adherence ?? 0}%`} />
        <Metric icon={Trophy} label="PR" value={d.prs?.length ?? 0} />
        <Metric icon={TrendingUp} label={t("weightChange")} value={d.weight_change != null ? `${d.weight_change > 0 ? "+" : ""}${d.weight_change}kg` : "—"} />
      </div>
      {shareUrl && (
        <div className="mt-4 rounded-xl border border-primary/30 bg-primary/5 p-3" data-testid="share-link-box">
          <div className="text-xs text-muted-foreground">{t("shareProgress")}</div>
          <div className="mt-1 flex items-center gap-2">
            <input readOnly value={shareUrl} className="flex-1 truncate rounded-md border bg-background px-2 py-1.5 text-xs" data-testid="share-link-input" />
            <Button size="sm" variant="outline" onClick={copyShare} className="gap-1 px-2" data-testid="copy-link-btn"><Copy className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="ghost" onClick={revokeShare} className="gap-1 px-2 text-destructive" data-testid="revoke-link-btn"><Link2Off className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
const Metric = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border p-4">
    <Icon className="h-4 w-4 text-muted-foreground" />
    <div className="mt-2 font-display text-2xl font-bold tabular-nums">{value}</div>
    <div className="text-xs text-muted-foreground">{label}</div>
  </div>
);

function ExerciseProgress() {
  const { t } = useI18n();
  const { data } = useQuery({ queryKey: ["exercise-progress"], queryFn: async () => (await api.get("/exercise-progress")).data });
  const list = data || [];
  const [exId, setExId] = useState("");
  useEffect(() => { if (list.length && !exId) setExId(list[0].exercise_id); }, [list, exId]);
  const sel = list.find((e) => e.exercise_id === exId);
  const chart = (sel?.points || []).map((p) => ({ date: (p.date || "").slice(5), weight: p.weight, e1rm: p.e1rm }));
  const last = sel?.points?.[sel.points.length - 1];

  return (
    <div className="rounded-2xl border bg-card p-5" data-testid="exercise-progress">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/15 text-primary"><TrendingUp className="h-4.5 w-4.5" /></span>
          <h3 className="font-display text-lg font-bold tracking-tight">{t("exerciseProgress")}</h3>
        </div>
        {list.length > 0 && (
          <Select value={exId} onValueChange={setExId}>
            <SelectTrigger className="w-full sm:w-64" data-testid="progress-exercise-select"><SelectValue placeholder={t("selectExercise")} /></SelectTrigger>
            <SelectContent>{list.map((e) => <SelectItem key={e.exercise_id} value={e.exercise_id}>{e.exercise_name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      {chart.length > 0 ? (
        <>
          <LineTrend data={chart} dataKey="weight" xKey="date" unit=" kg" height={240} />
          {last && (
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <div><span className="text-muted-foreground">{t("topSetWeight")}: </span><span className="font-display font-bold tabular-nums">{last.weight} kg × {last.reps}</span></div>
              <div><span className="text-muted-foreground">{t("estOneRm")}: </span><span className="font-display font-bold tabular-nums text-primary">{last.e1rm} kg</span></div>
            </div>
          )}
        </>
      ) : (
        <p className="py-8 text-center text-sm text-muted-foreground">{t("noProgressData")}</p>
      )}
    </div>
  );
}


function BestDays() {
  const { t, lang } = useI18n();
  const { data } = useQuery({ queryKey: ["analytics"], queryFn: async () => (await api.get("/analytics")).data });
  const labels = lang === "id" ? ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"] : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const chart = (data?.dow_counts || [0, 0, 0, 0, 0, 0, 0]).map((v, i) => ({ label: labels[i], value: v }));
  return (
    <div className="rounded-2xl border bg-card p-5">
      <h3 className="mb-4 font-display text-lg font-bold tracking-tight">{lang === "id" ? "Hari Latihan Terbaik" : "Best Training Days"}</h3>
      <Bars data={chart} height={200} />
    </div>
  );
}

function Goals() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: goals } = useQuery({ queryKey: ["goals"], queryFn: async () => (await api.get("/goals")).data });
  const { data: dash } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });

  const progressFor = (g) => {
    if (g.type === "target_weight" && dash?.weight && g.start != null) {
      const total = Math.abs(g.start - g.target) || 1;
      const done = Math.abs(g.start - dash.weight);
      return Math.min(100, Math.round((done / total) * 100));
    }
    if (g.type === "frequency") return Math.min(100, Math.round(((dash?.sessions_this_week || 0) / (g.target || 1)) * 100));
    return 0;
  };

  return (
    <div>
      <SectionTitle icon={Target} title={t("goals")}
        action={<Button size="sm" onClick={() => setOpen(true)} className="gap-1" data-testid="add-goal-btn"><Plus className="h-4 w-4" /> {t("addGoal")}</Button>} />
      {goals?.length ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {goals.map((g) => (
            <div key={g.id} className="rounded-2xl border bg-card p-5" data-testid={`goal-${g.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/15 text-primary"><Target className="h-4 w-4" /></span>
                  <div><div className="font-medium">{g.title}</div><div className="text-xs text-muted-foreground capitalize">{g.type.replace("_", " ")}</div></div>
                </div>
                <button onClick={async () => { await api.delete(`/goals/${g.id}`); qc.invalidateQueries({ queryKey: ["goals"] }); }} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="mt-4">
                <div className="mb-1.5 flex justify-between text-xs"><span className="text-muted-foreground">{progressFor(g)}%</span><span className="tabular-nums font-medium">{g.target} {g.unit}</span></div>
                <ProgressBar value={progressFor(g)} className="h-2" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon={Target} title={t("goals")} description={t("startTracking")} action={<Button onClick={() => setOpen(true)} className="gap-1"><Plus className="h-4 w-4" /> {t("addGoal")}</Button>} />
      )}
      <GoalDialog open={open} onClose={() => setOpen(false)} onSaved={() => qc.invalidateQueries({ queryKey: ["goals"] })} startWeight={dash?.weight} />
    </div>
  );
}

function GoalDialog({ open, onClose, onSaved, startWeight }) {
  const { t } = useI18n();
  const [f, setF] = useState({ type: "target_weight", title: "", target: "", unit: "kg" });
  const save = async () => {
    if (!f.title || !f.target) return toast.error("Fill all fields");
    const body = { ...f, target: parseFloat(f.target), status: "active" };
    if (f.type === "target_weight") { body.start = startWeight; body.unit = "kg"; }
    if (f.type === "frequency") body.unit = "sesi";
    await api.post("/goals", body);
    toast.success(t("save")); onSaved(); onClose(); setF({ type: "target_weight", title: "", target: "", unit: "kg" });
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("addGoal")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Type</Label>
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger className="mt-1.5" data-testid="goal-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="target_weight">{t("latestWeight")}</SelectItem><SelectItem value="frequency">{t("train")}</SelectItem><SelectItem value="body_fat">{t("bodyFat")}</SelectItem></SelectContent></Select>
          </div>
          <div><Label>{t("name")}</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1.5" data-testid="goal-title" /></div>
          <div><Label>Target</Label><Input type="number" value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} className="mt-1.5 tabular-nums" data-testid="goal-target" /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="save-goal">{t("save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PRs() {
  const { t, lang } = useI18n();
  const { data } = useQuery({ queryKey: ["analytics"], queryFn: async () => (await api.get("/analytics")).data });
  const prs = data?.prs || [];
  return (
    <div>
      <SectionTitle icon={Trophy} title={t("personalRecords")} />
      {prs.length ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {prs.map((p, i) => (
            <div key={p.exercise_id || p.exercise_name || i} className="flex items-center gap-3 rounded-2xl border bg-card p-4" data-testid={`pr-${i}`}>
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary"><Trophy className="h-5 w-5" /></span>
              <div><div className="font-medium">{p.exercise_name}</div><div className="text-xs text-muted-foreground">{lang === "id" ? "Estimasi 1RM" : "Est. 1RM"}: <span className="tabular-nums font-semibold text-foreground">{p.e1rm} kg</span></div></div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon={Trophy} title={t("personalRecords")} description={t("startTracking")} />
      )}
    </div>
  );
}
